// sfc32 (Small Fast Counting, Chris Doty-Humphrey). Én strøm pr. system: vejr, hændelser, kunder, bot.
// Tilstanden er fire uint32 i et almindeligt array, så den ligger direkte i State og kan hashes.

import type { RngStreams } from './types';

export type RngState = [number, number, number, number];

/** Næste uint32 fra strømmen. Muterer tilstanden. */
export function nextU32(s: RngState): number {
  let a = s[0] >>> 0;
  let b = s[1] >>> 0;
  let c = s[2] >>> 0;
  let d = s[3] >>> 0;
  const t = (((a + b) >>> 0) + d) >>> 0;
  d = (d + 1) >>> 0;
  a = b ^ (b >>> 9);
  b = (c + (c << 3)) >>> 0;
  c = ((c << 21) | (c >>> 11)) >>> 0;
  c = (c + t) >>> 0;
  s[0] = a >>> 0;
  s[1] = b >>> 0;
  s[2] = c;
  s[3] = d;
  return t;
}

/** Flydende tal i [0, 1). */
export function nextFloat(s: RngState): number {
  return nextU32(s) / 4294967296;
}

/** Heltal i [lo, hi] inklusive. */
export function nextInt(s: RngState, lo: number, hi: number): number {
  if (hi <= lo) return lo;
  return lo + Math.floor(nextFloat(s) * (hi - lo + 1));
}

/** Vælg ét element. */
export function pick<T>(s: RngState, arr: readonly T[]): T {
  if (arr.length === 0) throw new Error('pick fra tomt array');
  return arr[nextInt(s, 0, arr.length - 1)] as T;
}

/** Fisher-Yates på en kopi. */
export function shuffled<T>(s: RngState, arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = nextInt(s, 0, i);
    const tmp = out[i] as T;
    out[i] = out[j] as T;
    out[j] = tmp;
  }
  return out;
}

/** splitmix32: spreder et lille seed ud over 32 bit. */
function splitmix32(x: number): number {
  x = (x + 0x9e3779b9) >>> 0;
  let z = x;
  z = Math.imul(z ^ (z >>> 16), 0x85ebca6b) >>> 0;
  z = Math.imul(z ^ (z >>> 13), 0xc2b2ae35) >>> 0;
  return (z ^ (z >>> 16)) >>> 0;
}

/** Opret en strøm fra seed og en fast strøm-salt. Kører 15 runder for at blande. */
export function makeStream(seed: number, salt: number): RngState {
  let x = (seed ^ Math.imul(salt + 1, 0x27d4eb2d)) >>> 0;
  const s: RngState = [0, 0, 0, 0];
  for (let i = 0; i < 4; i++) {
    x = splitmix32(x + i);
    s[i] = x;
  }
  for (let i = 0; i < 15; i++) nextU32(s);
  return s;
}

export function makeStreams(seed: number): RngStreams {
  return {
    weather: makeStream(seed, 1),
    events: makeStream(seed, 2),
    customers: makeStream(seed, 3),
    bot: makeStream(seed, 4),
    smaa: makeStream(seed, 5),
    packs: makeStream(seed, 6),
  };
}
